import type { Context, FilterStand, Lijst } from '@xxtract/tabelfilters';
import { SERVICE_CODE } from '@/features/processes/processes.constants';
import { SERVICE_STATUSES } from './orders.api.constants';
import { KNOWN_SERVICE_CODES } from './orders.constants';
import { processStepBadge } from './tree-badges';
import type { OrderAggregate, ProductNode, TreeServiceView } from './orders.api';

/**
 * spec-tabelfilters story 11: kolombeschrijvingen van de orderlijst (extern gepagineerd, CAP-10), haar
 * paginafilter (zoekveld, tabblad, drempel, CAP-13) en de orderboom (browser).
 */

/** fu-6/AC2: de drie tabbladen van de orderlijst. */
export type OrdersTab = 'all' | 'attention' | 'onhold';

export const ORDERS_SLEUTEL = 'orders';
export const ORDERS_PAGINA_SLEUTEL = 'ordersPagina';
export const orderBoomSleutel = (orderNumber: string) => `orderBoom:${orderNumber}`;

export const ORDERS_PAGINAGROOTTE = 25;
/** De API weigert meer; een oude link met een grotere `pageSize` wordt vóór het verzoek begrensd. */
export const ORDERS_MAX_PAGINAGROOTTE = 100;

/** Oude links met een dienstcode van een van de acht diensten blijven werken. */
const DIENSTCODES = [...new Set<string>([...KNOWN_SERVICE_CODES, ...Object.values(SERVICE_CODE)])].sort();

/**
 * Sleutels van sorteerbare kolommen zijn de bronnamen, zodat `sort` ongewijzigd naar procesManagement gaat;
 * filters gebruiken de parameternamen van de bron (elk één waarde, `orders.routes.ts`).
 */
export const ORDERS_LIJST: Lijst<OrderAggregate> = {
  kolommen: [
    { sleutel: 'orderNumber', kopSleutel: 'orders.table.orderNumber', type: 'tekst', filter: false, waarde: (r) => r.orderNumber },
    { sleutel: 'klant', kopSleutel: 'orders.table.customer', type: 'keuzelijst', enkelvoudig: true, bron: 'klantenlijst', urlNaam: 'gln', sorteer: false, waarde: (r) => r.gln },
    { sleutel: 'dienst', kopSleutel: 'orders.table.services', type: 'keuzelijst', enkelvoudig: true, vasteWaarden: DIENSTCODES, urlNaam: 'serviceCode', sorteer: false, waarde: () => null },
    {
      sleutel: 'status',
      kopSleutel: 'orders.filters.status',
      type: 'keuzelijst',
      enkelvoudig: true,
      vasteWaarden: SERVICE_STATUSES,
      waardeLabel: (w, t) => t(`orders.serviceStatus.${w}`),
      sorteer: false,
      waarde: () => null,
    },
    { sleutel: 'createdAt', kopSleutel: 'orders.table.createdAt', type: 'datum', urlVan: 'from', urlTot: 'to', waarde: (r) => r.createdAt },
    { sleutel: 'lastEventAt', kopSleutel: 'orders.table.lastEventAt', type: 'datum', filter: false, waarde: (r) => r.lastEventAt },
  ],
  maxSorteerkolommen: 1,
  standaardSortering: () => [{ sleutel: 'createdAt', richting: 'af' }],
};

/** Zoekveld, tabblad "aandacht" en stilstanddrempel: paginafilter naast de kolomfilters (CAP-13). */
export const ORDERS_PAGINAFILTER: Lijst<unknown> = {
  alleenKolomfilters: true,
  kolommen: [
    { sleutel: 'search', kopSleutel: 'orders.filters.search', type: 'tekst', waarde: () => null },
    { sleutel: 'attention', kopSleutel: 'orders.tabs.attentionNoCount', type: 'keuzelijst', enkelvoudig: true, vasteWaarden: ['true'], waarde: () => null },
    { sleutel: 'stalenessDays', kopSleutel: 'orders.filters.stalenessDays', type: 'tekst', waarde: () => null },
  ],
};

/** spec-orders-geweigerd-filter: de weigerbare API-velden, met hun label. */
export const ORDER_FILTER_VELDEN = {
  gln: 'orders.filters.gln',
  serviceCode: 'orders.filters.serviceCode',
  status: 'orders.filters.status',
  from: 'orders.filters.from',
  to: 'orders.filters.to',
  stalenessDays: 'orders.filters.stalenessDays',
  search: 'orders.filters.search',
  sort: 'tabel.sorteerOp',
} as const;

export interface OrdersPaginafilter {
  search?: string;
  attention?: true;
  stalenessDays?: number;
}

/** Paginafilterstand → verzoekparameters. Een ongeldige drempel gaat door; de API weigert hem (400). */
export function paginafilterParameters(filters: FilterStand | undefined): OrdersPaginafilter {
  const search = typeof filters?.search === 'string' && filters.search.trim() !== '' ? filters.search : undefined;
  const drempel = typeof filters?.stalenessDays === 'string' && filters.stalenessDays !== '' ? Number(filters.stalenessDays) : undefined;
  const attention = Array.isArray(filters?.attention) && filters.attention.includes('true');
  return { ...(search ? { search } : {}), ...(attention ? { attention: true as const } : {}), ...(drempel !== undefined ? { stalenessDays: drempel } : {}) };
}

/** Het tabblad volgt uit de stand: aandacht gaat voor onhold, anders alle. */
export function tabUitStand(paginafilters: FilterStand | undefined, filters: FilterStand): OrdersTab {
  if (Array.isArray(paginafilters?.attention) && paginafilters.attention.includes('true')) return 'attention';
  const status = filters.status;
  return Array.isArray(status) && status.length === 1 && status[0] === 'onhold' ? 'onhold' : 'all';
}

/** De orderboom vertaalt het processtaplabel met parameters ("stap 2 van 5"). */
export interface OrderBoomContext extends Context {
  t: (sleutel: string, opties?: Record<string, unknown>) => string;
}

/** Laatste gebeurtenis over de diensten. */
export function laatsteActiviteit(services: TreeServiceView[]): string | null {
  const tijden = services.map((s) => s.lastEvent?.timestamp).filter((w): w is string => w != null);
  return tijden.length === 0 ? null : tijden.sort().at(-1)!;
}

/** Filteren en sorteren gaan over de producten; lagen blijven onder hun product. Keuzes op de eerste dienst. */
export const ORDERBOOM_LIJST: Lijst<ProductNode, OrderBoomContext> = {
  kolommen: [
    // Dezelfde terugval als de cel, zodat filteren op de zichtbare naam treft.
    { sleutel: 'naam', kopSleutel: 'orders.tree.columns.name', type: 'tekst', waarde: (r, ctx) => r.description ?? ctx.t('orders.tree.unnamedProduct') },
    { sleutel: 'gtin', kopSleutel: 'orders.tree.columns.gtin', type: 'tekst', waarde: (r) => r.gtin },
    {
      sleutel: 'processtap',
      kopSleutel: 'orders.tree.columns.processStep',
      type: 'keuzelijst',
      waarde: (r, ctx) => {
        const dienst = r.services[0];
        if (!dienst) return null;
        const badge = processStepBadge(dienst);
        return ctx.t(badge.labelKey, badge.labelParams);
      },
    },
    {
      sleutel: 'servicestatus',
      kopSleutel: 'orders.tree.columns.serviceStatus',
      type: 'keuzelijst',
      waardeLabel: (w, t) => t(`orders.serviceStatus.${w}`),
      waarde: (r) => r.services[0]?.serviceStatus ?? null,
    },
    { sleutel: 'laatsteEvent', kopSleutel: 'orders.tree.columns.lastEvent', type: 'datum', waarde: (r) => laatsteActiviteit(r.services) },
  ],
};
